import { useId, type ComponentProps } from 'react'
import { FieldError } from './field'

/**
 * A box to tick, with its label beside it and, under the label, a hint and an error, read out
 * after it. Click the label or the box alike.
 */
export function Checkbox({
  label,
  hint,
  error,
  ...props
}: Omit<ComponentProps<'input'>, 'type' | 'id'> & {
  label: string
  hint?: string
  /** The error to show under it, already translated. */
  error?: string | null
}) {
  const id = useId()
  const hintId = `${id}-hint`
  const errorId = `${id}-error`
  const describedBy = [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ')
  return (
    <div className="flex items-start gap-3 text-sm">
      <input
        id={id}
        type="checkbox"
        className="mt-0.5 size-4 shrink-0 accent-primary disabled:opacity-60"
        aria-describedby={describedBy || undefined}
        aria-invalid={error ? true : undefined}
        {...props}
      />
      <div className="min-w-0">
        <label htmlFor={id} className="font-medium">
          {label}
        </label>
        {hint && (
          <p id={hintId} className="mt-0.5 text-xs text-ink-muted">
            {hint}
          </p>
        )}
        <FieldError id={errorId}>{error}</FieldError>
      </div>
    </div>
  )
}
